import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl, decryptInviteToken } from '../../util/api';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import { pickUserFieldsData, addScopePrefix } from '../../util/userHelpers';
import { returnPathFromLoginSearch } from '../../util/sessionExpiredLogin';

import { authInfo, login, authenticationInProgress, signup, signupWithIdp, logout } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';

import {
  Page,
  Heading,
  NamedRedirect,
  LinkTabNavHorizontal,
  SocialLoginButton,
  ResponsiveBackgroundImageContainer,
  Modal,
  LayoutSingleColumn,
  Button,
  NamedLink,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import SignupForm from './SignupForm/SignupForm';
import SignupFormLink from './SignupForm/SignupFormLink';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import EmailVerificationInfo from './EmailVerificationInfo';
import DevAuthTestForms from './DevAuthTestForms';

// We need to get ToS asset and get it rendered for the modal on this page.
import { TermsOfServiceContent } from '../../containers/TermsOfServicePage/TermsOfServicePage';

// We need to get PrivacyPolicy asset and get it rendered for the modal on this page.
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';

import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { TOS_ASSET_NAME, PRIVACY_POLICY_ASSET_NAME } from './AuthenticationPage.duck';

import css from './AuthenticationPage.module.css';
import { FacebookLogo, GoogleLogo, LinkedInLogo, MicrosoftLogo } from './socialLoginLogos';
import { idpAuthErrorMessageId } from '../../util/idpAuthErrorMessageId';
import LoginBg from './img/login1.jpg';
import SignupBg from './img/login2.jpg';
import AgencyImg from './img/agency.svg';
import CandidateImg from './img/candidate.svg';
import CustomerImg from './img/customer.svg';
import logoImage from '../../assets/siteicon.png';
import { message } from 'antd';
import { userTypeOf } from '../../util/userType';
import { teamRoleOf } from '../../util/teamRole';
import UserTypeLabel from '../../components/UserTypeLabel/UserTypeLabel';

const SSO_AUTH_FLOW_KEY = 'ssoAuthFlow';
const SSO_SELECTED_USER_TYPE_KEY = 'ssoSelectedUserType';
const SSO_USER_TYPE_MISMATCH_ERROR_KEY = 'ssoUserTypeMismatchError';

// Social login buttons are needed by AuthenticationForms
export const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const { isLogin, showFacebookLogin, showGoogleLogin, showLinkedInLogin, showMicrosoftLogin, from, userType, isMobileLayout, applyWorkId } = props;
  const showSocialLogins = showFacebookLogin || showGoogleLogin || showLinkedInLogin || showMicrosoftLogin;

  const persistSsoContext = () => {
    if (typeof window === 'undefined') return;

    const authFlow = isLogin ? 'login' : 'signup';
    window.localStorage.setItem(SSO_AUTH_FLOW_KEY, authFlow);

    const selectedSignupUserType = userType || window.localStorage.getItem('selectUserType');

    if (
      authFlow === 'signup' &&
      selectedSignupUserType &&
      selectedSignupUserType !== 'undefined'
    ) {
      window.localStorage.setItem(SSO_SELECTED_USER_TYPE_KEY, selectedSignupUserType);
    } else {
      window.localStorage.removeItem(SSO_SELECTED_USER_TYPE_KEY);
    }
  };

  const getDataForSSORoutes = () => {
    const baseUrl = apiBaseUrl();

    // Default route where user is returned after successfull authentication
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);

    // Route for confirming user data before creating a new user
    let defaultConfirm;
    if (applyWorkId)
      defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration) + '?applyWorkId=' + applyWorkId;
    else
      defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);

    const queryParams = new URLSearchParams({
      ...(defaultReturn ? { defaultReturn } : {}),
      ...(defaultConfirm ? { defaultConfirm } : {}),
      ...(isLogin ? { authFlow: 'login' } : { authFlow: 'signup' }),
      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      ...(from ? { from } : {}),
      // The preselected userType needs to be saved over the visit to identity provider's service
      ...(userType ? { userType } : {}),
    });

    return { baseUrl, queryParams: queryParams.toString() };
  };

  const authWithFacebook = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/facebook?${queryParams}`;
  };

  const authWithGoogle = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/google?${queryParams}`;
  };

  const authWithLinkedIn = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/linkedin?${queryParams}`;
  };

  const authWithMicrosoft = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/microsoft?${queryParams}`;
  };

  return showSocialLogins ? (
    <div className={isMobileLayout ? css.mobileidpButtons : css.idpButtons}>
      {/* <div className={css.socialButtonsOr}>
        <span className={css.socialButtonsOrText}>
          <FormattedMessage id="AuthenticationPage.or" />
        </span>
      </div> */}

      {showFacebookLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithFacebook()}>
            <span className={css.buttonIcon}>{FacebookLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={css.buttonIcon}>{GoogleLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showLinkedInLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithLinkedIn()}>
            <span className={css.buttonIcon}>{LinkedInLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithLinkedIn" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithLinkedIn" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showMicrosoftLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithMicrosoft()}>
            <span className={css.buttonIcon}>{MicrosoftLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithMicrosoft" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithMicrosoft" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

    </div>
  ) : null;
};

const getNonUserFieldParams = (values, userFieldConfigs) => {
  const userFieldKeys = userFieldConfigs.map(({ scope, key }) => addScopePrefix(scope, key));

  return Object.entries(values).reduce((picked, [key, value]) => {
    const isUserFieldKey = userFieldKeys.includes(key);

    return isUserFieldKey
      ? picked
      : {
        ...picked,
        [key]: value,
      };
  }, {});
};

// Tabs for SignupForm and LoginForm
export const AuthenticationForms = props => {
  const {
    isLogin,
    isSignupLink,
    showFacebookLogin,
    showGoogleLogin,
    showLinkedInLogin,
    showMicrosoftLogin,
    userType,
    from,
    submitLogin,
    loginError,
    idpAuthError,
    signupError,
    authInProgress,
    submitSignup,
    termsAndConditions,
    onManageDisableScrolling,
  } = props;

  const [selectUserType, setSelectUserType] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [isInviteExpired, setIsInviteExpired] = useState(false);

  let isMobileLayout = false;

  if (typeof window !== 'undefined') {
    isMobileLayout = window.innerWidth <= 768;
  }

  let inviteLink = '';

  if (typeof window !== 'undefined') {
    inviteLink = window?.location?.search ? window?.location?.search?.replace('?', '') : null;
  }

  useEffect(() => {
    if (!inviteLink) return undefined;

    let cancelled = false;

    // Decryption happens server-side (/api/decryptInviteToken) — the AES secret
    // that encrypted this token must never be shipped to the client bundle.
    const resolveInviteLink = async () => {
      try {
        const LinkFromParams = inviteLink.replace(/^"|"$/g, '');
        const response = await decryptInviteToken(LinkFromParams);
        const decryptedData = response?.data;
        if (cancelled || !decryptedData) return;

        if (decryptedData?.expiresAt && Date.now() > decryptedData.expiresAt) {
          setIsInviteExpired(true);
          return; // stop processing, don't set invite data
        }
        if (decryptedData?.role) {
          window.localStorage.setItem('inviteRole', decryptedData.role);
        } else {
          window.localStorage.removeItem('inviteRole');
        }
        if (decryptedData?.listingId) {
          window.localStorage.setItem('inviteListingId', decryptedData.listingId);
        } else {
          window.localStorage.removeItem('inviteListingId');
        }
        // New-candidate activation: persist the pending-candidate key so it survives
        // the SSO round-trip and can prefill onboarding + link the apply tx on submit.
        if (decryptedData?.pendingCandidateId) {
          window.localStorage.setItem('pendingCandidateId', decryptedData.pendingCandidateId);
        } else {
          window.localStorage.removeItem('pendingCandidateId');
        }
        // A Preferred Network invitation: kept so that the first profile save can accept it.
        if (decryptedData?.networkInvite) {
          window.localStorage.setItem('networkInviteToken', LinkFromParams);
        } else {
          window.localStorage.removeItem('networkInviteToken');
        }
        setInviteData(decryptedData);
        updateSelectUserType(decryptedData?.userType);

        // Store the encrypted invite link so we can redirect back on email mismatch
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('inviteLink', inviteLink);
        }
      } catch (e) {
        // Query string is not a valid encrypted invite link — ignore silently
        console.warn('Failed to decrypt invite link from query string:', e.message);
      }
    };

    resolveInviteLink();

    return () => {
      cancelled = true;
    };
  }, []);



  const config = useConfiguration();
  const { userFields, userTypes = [] } = config.user;
  const preselectedUserType = userTypes.find(conf => conf.userType === userType)?.userType || null;

  const isCandidate = userType === 'candidate';

  const fromMaybe = from ? { from } : null;
  const signupRouteName = !!preselectedUserType ? 'SignupForUserTypePage' : 'SignupPage';
  const userTypeMaybe = preselectedUserType ? { userType: preselectedUserType } : null;
  const fromState = { state: { ...fromMaybe, ...userTypeMaybe } };
  const tabs = [
    {
      text: (
        <Heading as={!isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.signupLinkText" />
        </Heading>
      ),
      selected: !isLogin,
      linkProps: {
        name: signupRouteName,
        params: userTypeMaybe,
        to: fromState,
      },
    },
    {
      text: (
        <Heading as={isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.loginLinkText" />
        </Heading>
      ),
      selected: isLogin,
      linkProps: {
        name: 'LoginPage',
        to: fromState,
      },
    },
  ];

  const handleSubmitSignup = values => {
    const { userType: rawUserType, email, password, fname, lname, displayName, phoneNumber, role: rawRole, profileListingId: rawProfileListingId, ...rest } = values;
    const sanitize = v => (v && v !== 'undefined' ? v : undefined);
    const userType = sanitize(rawUserType) || 'candidate';
    const role = sanitize(rawRole);
    const profileListingId = sanitize(rawProfileListingId);
    const displayNameMaybe = displayName ? { displayName: displayName.trim() } : {};

    const params = {
      email,
      password,
      firstName: fname.trim(),
      lastName: lname.trim(),
      ...displayNameMaybe,
      publicData: {
        userType,
        phoneNumber,
        role,
        profileListingId,
        ...pickUserFieldsData(rest, 'public', userType, userFields),
      },
      privateData: {
        ...pickUserFieldsData(rest, 'private', userType, userFields),
      },
      protectedData: {
        phoneNumber,
        ...pickUserFieldsData(rest, 'protected', userType, userFields),
        ...getNonUserFieldParams(rest, userFields),
      },
    };

    submitSignup(params);
  };

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const idpAuthErrorMessage = (
    <div className={css.error}>
      {idpAuthError?.code === 'organization-access-removed' ? (
        <span>Your organization access has been removed. Please contact your administrator.</span>
      ) : idpAuthError?.code === 'account-deactivated' ? (
        <span>This account has been deactivated. Please contact your administrator.</span>
      ) : idpAuthError?.code === 'invitation-revoked' ? (
        <span>This invitation has been revoked by your organization admin.</span>
      ) : (
        <FormattedMessage
          id={idpAuthErrorMessageId(idpAuthError?.code)}
          values={{
            userType: idpAuthError?.existingUserType || '',
            selectedUserType: idpAuthError?.selectedUserType || '',
          }}
        />
      )}
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  const loginOrSignupError =
    isLogin && !!idpAuthError
      ? idpAuthErrorMessage
      : isLogin && !!loginError
        ? loginErrorMessage
        : !!signupError
          ? signupErrorMessage
          : null;


  const updateSelectUserType = (value) => {
    setSelectUserType(value);
    if (typeof localStorage !== 'undefined') {
      if (value && value !== 'undefined') {
        localStorage.setItem('selectUserType', value);
      } else {
        localStorage.removeItem('selectUserType');
      }
    }
  };

  const SignUpCards = () => {
    return (
      <>
        <div className={css.signUpCards}>
          <div className={css.cardSection}>
            <div className={css.card}>
              <div className={css.cardHead}>
                <div>
                  <img src={CandidateImg} alt="Candidate" />
                </div>
                <div>
                  <h4>Candidate</h4>
                  <p><b>Empower your job search:</b> Create your profile, discover tailored opportunities, and take the next step in your career journey. Stand out with verified credentials that build trust from day one.</p>
                </div>
              </div>
              <div>
                <Button
                  className={css.cardBtn}
                  onClick={() => {
                    updateSelectUserType('candidate');
                  }}
                >
                  Sign Up
                </Button>
              </div>
            </div>
            <div className={css.card}>
              <div className={css.cardHead}>
                <div>
                  <img src={AgencyImg} alt="Supplier" />
                </div>
                <div>
                  <h4>Supplier</h4>
                  <p><b>Grow your business across staffing and services:</b> Showcase your capabilities, verify your consultants, and sell technology implementation services through RFP participation. Respond to qualified job posts and RFPs, collaborate with enterprise customers, and win more business in a transparent, trust-driven marketplace.</p>
                </div>
              </div>
              <div>
                <Button
                  className={css.cardBtn}
                  onClick={() => {
                    updateSelectUserType('agency');
                  }}
                >
                  Sign Up
                </Button>
              </div>
            </div>
            <div className={css.card}>
              <div className={css.cardHead}>
                <div>
                  <img src={CustomerImg} alt="Customer" />
                </div>
                <div>
                  <h4>Customer</h4>
                  <p><b>Source talent and services with confidence</b> Access verified consultants, invite trusted suppliers, and buy technology implementation services by publishing RFPs. Accelerate procurement, reduce risk, and streamline your staffing and project sourcing workflows—all in one verification-powered platform.</p>
                </div>
              </div>
              <div>
                <Button
                  className={css.cardBtn}
                  onClick={() => {
                    updateSelectUserType('customer');
                  }}
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </div>

          <LinkTabNavHorizontal className={css.tabs} tabs={tabs} isCandidate={isCandidate} />

        </div>
      </>
    );
  };

  if (isSignupLink) {
    if (isInviteExpired) {
      return (
        <div className={css.content}>
          <div className={css.authBoxSignupLink}>
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <h2 style={{ color: '#d9363e', marginBottom: '16px' }}>Invite Link Expired</h2>
              <p style={{ fontSize: '16px', color: '#484848' }}>
                This invite link has expired. Please ask your Organization admin to send a new invitation.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={css.content}>
        <>
          <div className={css.authBoxSignupLink}>
            <div>
              {loginOrSignupError}

              <SignupFormLink
                className={css.signupForm}
                selectUserType={selectUserType}
                inviteData={inviteData}
              />
            </div>
          </div>
        </>
      </div>
    );
  }
  return (
    <div className={css.content}>
      {isLogin || selectUserType ?
        <>
          <div className={css.authBox}>
            <div className={css.authImage}>
              {isLogin ? (
                <img src={LoginBg} alt="loginbg" />
              ) : (
                <img src={SignupBg} alt="signupbg" />
              )}
            </div>
            <div className={css.authContainer}>
              {!isLogin && selectUserType && (
                <button
                  className={css.backButton}
                  onClick={() => setSelectUserType(null)}
                  type="button"
                >
                  <span className={css.backArrow}>←</span>
                  <span className={css.backUserType}><UserTypeLabel userType={selectUserType} /></span>
                </button>
              )}

              <LinkTabNavHorizontal className={css.tabs} tabs={tabs} isCandidate={isCandidate} />

              {isLogin && (
                <div className={css.welcomeSection}>
                  <h1 className={css.welcomeTitle}>Welcome Back</h1>
                  <p className={css.welcomeSubtitle}>Sign in to your account to continue.</p>
                </div>
              )}

              {!isLogin && (
                <div className={css.welcomeSection}>
                  <h1 className={css.welcomeTitle}>Create Account</h1>
                  <p className={css.welcomeSubtitle}>Sign up to get started.</p>
                </div>
              )}

              {!isLogin && selectUserType && (
                <p className={css.userTypeDescription}>
                  {selectUserType === 'candidate' && 'Create an account, verify your claims, and apply for jobs.'}
                  {selectUserType === 'agency' && 'Create an account, verify your company and employee claims, and apply for jobs and RFPs.'}
                  {selectUserType === 'customer' && 'Create an account, build preferred supplier and talent pools, post jobs, and issue RFPs.'}
                </p>
              )}

              {loginOrSignupError}

              {!isLogin && (
                <p className={css.privacyPolicyNote}>
                  By signing up, you agree to our{' '}
                  <NamedLink className={css.privacyPolicyLink} name="TermsAndConditionsCustomPage">
                    Terms and Conditions
                  </NamedLink>
                  {' '}and{' '}
                  <NamedLink className={css.privacyPolicyLink} name="PrivacyPolicyCustomPage">
                    Privacy Policy
                  </NamedLink>
                </p>
              )}

              <div className={css.socialButtonsCard}>
                <SocialLoginButtonsMaybe
                  isLogin={isLogin}
                  showFacebookLogin={showFacebookLogin}
                  showGoogleLogin={showGoogleLogin}
                  showLinkedInLogin={true}
                  showMicrosoftLogin={true}
                  isMobileLayout={isMobileLayout}
                  {...fromMaybe}
                  {...userTypeMaybe}
                />
              </div>

              <DevAuthTestForms
                isLogin={isLogin}
                selectUserType={selectUserType}
                submitLogin={submitLogin}
                handleSubmitSignup={handleSubmitSignup}
                authInProgress={authInProgress}
                userTypes={userTypes}
                userFields={userFields}
                updateSelectUserType={updateSelectUserType}
                inviteData={inviteData}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
          </div>
        </> :
        <SignUpCards />}
    </div>
  );
};

// Form for confirming information from IdP (e.g. Facebook)
// This is shown before new user is created to Marketplace API
const ConfirmIdProviderInfoForm = props => {
  const {
    userType,
    authInfo,
    authInProgress,
    confirmError,
    submitSingupWithIdp,
    termsAndConditions,
  } = props;
  const config = useConfiguration();
  const { userFields, userTypes } = config.user;
  const preselectedUserType = userTypes.find(conf => conf.userType === userType)?.userType || null;

  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

  const handleSubmitConfirm = values => {
    const { idpToken, email, firstName, lastName, idpId } = authInfo;

    const {
      userType: rawUserType,
      email: newEmail,
      firstName: newFirstName,
      lastName: newLastName,
      displayName,
      role: rawRole,
      profileListingId: rawProfileListingId,
      ...rest
    } = values;

    const sanitize = v => (v && v !== 'undefined' ? v : undefined);
    const userType = sanitize(rawUserType) || 'candidate';
    const role = sanitize(rawRole);
    const profileListingId = sanitize(rawProfileListingId);

    const displayNameMaybe = displayName ? { displayName: displayName.trim() } : {};
    const confirmedEmail = newEmail || email;

    // Pass email, fistName or lastName to Marketplace API only if user has edited them
    // and they can't be fetched directly from idp provider (e.g. Facebook)

    const authParams = {
      ...(confirmedEmail && { email: confirmedEmail }),
      ...(newFirstName !== firstName && { firstName: newFirstName }),
      ...(newLastName !== lastName && { lastName: newLastName }),
    };

    // Pass other values as extended data according to user field configuration
    const extendedDataMaybe = !isEmpty(rest)
      ? {
        publicData: {
          userType,
          role,
          profileListingId,
          ...pickUserFieldsData(rest, 'public', userType, userFields),
        },
        privateData: {
          ...pickUserFieldsData(rest, 'private', userType, userFields),
        },
        protectedData: {
          ...pickUserFieldsData(rest, 'protected', userType, userFields),
          // If the confirm form has any additional values, pass them forward as user's protected data
          ...getNonUserFieldParams(rest, userFields),
        },
      }
      : {};

    return submitSingupWithIdp({
      idpToken,
      idpId,
      ...authParams,
      ...displayNameMaybe,
      ...extendedDataMaybe,
    });
  };

  const confirmErrorMessageText = confirmError?.message;
  const isInvitationRevokedError = confirmError?.name === 'invitation-revoked';

  const confirmErrorMessage = confirmError ? (
    <div className={css.error}>
      {isSignupEmailTakenError(confirmError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : isInvitationRevokedError ? (
        <span>This invitation has been revoked by your organization admin.</span>
      ) : confirmErrorMessageText ? (
        <span>{confirmErrorMessageText}</span>
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  ) : null;

  return (
    <div className={css.content}>
      {/* <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
        <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
      </Heading>

      <p className={css.confirmInfoText}>
        <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
      </p> */}
      {confirmErrorMessage}
      <ConfirmSignupForm
        className={css.form}
        onSubmit={handleSubmitConfirm}
        inProgress={authInProgress}
        termsAndConditions={termsAndConditions}
        authInfo={authInfo}
        idp={idp}
        preselectedUserType={preselectedUserType}
        userTypes={userTypes}
        userFields={userFields}
      />
    </div>
  );
};

export const AuthenticationOrConfirmInfoForm = props => {
  const {
    tab,
    userType,
    authInfo,
    from,
    showFacebookLogin,
    showGoogleLogin,
    showLinkedInLogin,
    showMicrosoftLogin,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    authInProgress,
    loginError,
    idpAuthError,
    signupError,
    confirmError,
    termsAndConditions,
    onManageDisableScrolling,
  } = props;
  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';
  const isSignupLink = tab === 'signuplink';

  return isConfirm ? (
    <ConfirmIdProviderInfoForm
      userType={userType}
      authInfo={authInfo}
      submitSingupWithIdp={submitSingupWithIdp}
      authInProgress={authInProgress}
      confirmError={confirmError}
      termsAndConditions={termsAndConditions}
    />
  ) : (
    <AuthenticationForms
      isLogin={isLogin}
      isSignupLink={isSignupLink}
      showFacebookLogin={showFacebookLogin}
      showGoogleLogin={showGoogleLogin}
      showLinkedInLogin={true}
      showMicrosoftLogin={true}
      userType={userType}
      from={from}
      loginError={loginError}
      idpAuthError={idpAuthError}
      signupError={signupError}
      submitLogin={submitLogin}
      authInProgress={authInProgress}
      submitSignup={submitSignup}
      termsAndConditions={termsAndConditions}
      onManageDisableScrolling={onManageDisableScrolling}
    ></AuthenticationForms>
  );
};

const getAuthInfoFromCookies = () => {
  return Cookies.get('st-authinfo')
    ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
    : null;
};
const getAuthErrorFromCookies = () => {
  return Cookies.get('st-autherror')
    ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
    : null;
};

const getSsoUserTypeMismatchErrorFromStorage = () => {
  if (typeof window === 'undefined') return null;

  const rawError = window.localStorage.getItem(SSO_USER_TYPE_MISMATCH_ERROR_KEY);
  if (!rawError) return null;

  try {
    const parsed = JSON.parse(rawError);
    if (!parsed?.existingUserType) {
      return null;
    }

    return {
      code: 'sso-user-type-mismatch',
      existingUserType: parsed.existingUserType,
      selectedUserType: parsed.selectedUserType,
      message: parsed.message,
    };
  } catch (e) {
    window.localStorage.removeItem(SSO_USER_TYPE_MISMATCH_ERROR_KEY);
    return null;
  }
};

const BlankPage = props => {
  const { schemaTitle, schemaDescription, scrollingDisabled } = props;
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <div className={css.loadingOverlay}>
        <div className={css.loadingSpinner}>
          <img src={logoImage} alt="Loading" className={css.spinningLogo} />
        </div>
      </div>
    </Page>
  );
};

/**
 * The AuthenticationPage component.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.authInProgress - Whether the authentication is in progress
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {propTypes.error} props.loginError - The login error
 * @param {propTypes.error} props.signupError - The signup error
 * @param {propTypes.error} props.confirmError - The confirm error
 * @param {Function} props.submitLogin - The login submit function
 * @param {Function} props.submitSignup - The signup submit function
 * @param {Function} props.submitSingupWithIdp - The signup with IdP submit function
 * @param {'login' | 'signup'| 'confirm'} props.tab - The tab to render
 * @param {boolean} props.sendVerificationEmailInProgress - Whether the verification email is in progress
 * @param {propTypes.error} props.sendVerificationEmailError - The verification email error
 * @param {Function} props.onResendVerificationEmail - The resend verification email function
 * @param {Function} props.onManageDisableScrolling - The manage disable scrolling function
 * @param {object} props.privacyAssetsData - The privacy assets data
 * @param {boolean} props.privacyFetchInProgress - Whether the privacy fetch is in progress
 * @param {propTypes.error} props.privacyFetchError - The privacy fetch error
 * @param {object} props.tosAssetsData - The terms of service assets data
 * @param {boolean} props.tosFetchInProgress - Whether the terms of service fetch is in progress
 * @param {propTypes.error} props.tosFetchError - The terms of service fetch error
 * @param {object} props.location - The location object
 * @param {object} props.params - The path parameters
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @returns {JSX.Element}
 */
export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(
    () => getAuthErrorFromCookies() || getSsoUserTypeMismatchErrorFromStorage()
  );
  // A return page carried in the address (the session timer sends one after a full reload, where
  // router state is lost). Read once: the session-expired message clears the query right away.
  const [queryFrom] = useState(() => returnPathFromLoginSearch(props.location?.search));
  const [mounted, setMounted] = useState(false);

  const config = useConfiguration();
  const intl = useIntl();

  useEffect(() => {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    if (authError) {
      Cookies.remove('st-autherror');

      if (authError?.code === 'sso-user-type-mismatch' && typeof window !== 'undefined') {
        window.localStorage.removeItem(SSO_USER_TYPE_MISMATCH_ERROR_KEY);
      }
    }
    setMounted(true);
  }, []);

  // Check for sessionExpired query parameter
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sessionExpired') === 'true') {
        message.error("Your session has expired. Please log in again.");
        // Clean up the URL without reloading (reload would destroy the message notification)
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  const {
    authInProgress,
    authInfoLoaded,
    currentUser,
    isAuthenticated,
    location,
    params: pathParams,
    loginError,
    scrollingDisabled,
    signupError,
    submitLogin,
    submitSignup,
    confirmError,
    submitSingupWithIdp,
    tab = 'signup',
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onManageDisableScrolling,
    onLogout,
    onFetchAuthInfo,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  } = props;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!authInfoLoaded) {
      onFetchAuthInfo();
    }
  }, [authInfoLoaded, onFetchAuthInfo]);

  // "Start free trial" intent from the topbar CTA. Passed via router state (not a
  // query param — the query string here is parsed as an invite link). Persist it in
  // localStorage so it survives the signup → email-verification → onboarding hops,
  // where router state and URL params are lost. Consumed when onboarding completes
  // (see ProfileSettingsPage), which then opens the subscriptions page.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.state?.freeTrial === true) {
      window.localStorage.setItem('redirectToSubscriptions', '1');
    } else if (tab !== 'confirm') {
      // Arrived on signup/login without the CTA intent (normal signup or "back").
      // Clear any stale marker so a plain signup doesn't inherit the trial flow.
      // Skip the 'confirm' (SSO) tab, which is a legit hop within the trial flow.
      window.localStorage.removeItem('redirectToSubscriptions');
    }
  }, [location.state, tab]);

  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  const from = locationFrom || authinfoFrom || queryFrom || null;

  const currentUserHasListing = currentUser?.attributes?.profile?.publicData?.hasListing;

  // Check if SSO email matches the invited email
  useEffect(() => {
    if (typeof window !== 'undefined' && tab === 'confirm' && authInfo?.email) {
      const invitedMail = window.localStorage.getItem('invitedMail');
      if (invitedMail && invitedMail.toLowerCase() !== authInfo.email.toLowerCase()) {
        // Email mismatch - set error flag and redirect back to signup link page
        window.localStorage.setItem('emailMismatchError', 'true');
        Cookies.remove('st-authinfo');
        const storedInviteLink = window.localStorage.getItem('inviteLink');
        if (storedInviteLink) {
          window.location.href = `/signuplink?${storedInviteLink}`;
        }
      } else if (invitedMail) {
        // Emails match - clean up invite localStorage items
        window.localStorage.removeItem('invitedMail');
        window.localStorage.removeItem('inviteLink');
      }
    }
  }, [tab, authInfo]);

  const isConfirm = tab === 'confirm';
  const userTypeInPushState = location.state?.userType || null;
  const userTypeInAuthInfo = isConfirm && authInfo?.userType ? authInfo?.userType : null;
  const userType = pathParams?.userType || userTypeInPushState || userTypeInAuthInfo || null;

  const { userTypes = [] } = config.user;
  const preselectedUserType = userTypes.find(conf => conf.userType === userType)?.userType || null;
  const show404 = userType && !preselectedUserType;

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isLogin = tab === 'login';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLogin) {
      window.localStorage.removeItem('selectUserType');
    }
  }, [isLogin]);

  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const existingUserType = userTypeOf(currentUser);

  const ssoAuthFlow =
    typeof window !== 'undefined' ? window.localStorage.getItem(SSO_AUTH_FLOW_KEY) : null;
  const ssoSelectedUserType =
    typeof window !== 'undefined'
      ? window.localStorage.getItem(SSO_SELECTED_USER_TYPE_KEY) ||
        window.localStorage.getItem('selectUserType')
      : null;

  const shouldShowAuthLoader =
    isLogin &&
    !authError &&
    (!mounted || !authInfoLoaded || authInProgress || (isAuthenticated && !currentUserLoaded));

  const pendingSsoSignupUserTypeCheck =
    ssoAuthFlow === 'signup' && isAuthenticated && !!ssoSelectedUserType && !currentUserLoaded;

  const isSsoSignupUserTypeMismatch =
    ssoAuthFlow === 'signup' &&
    isAuthenticated &&
    currentUserLoaded &&
    !!existingUserType &&
    !!ssoSelectedUserType &&
    existingUserType !== ssoSelectedUserType;

  const isNotCandidate = userTypeOf(currentUser) !== 'candidate';
  const isNotAdmin = teamRoleOf(currentUser) !== undefined && teamRoleOf(currentUser) !== 'Admin';
  const hasNotJoined = publicData?.addedUserJoined !== true;
  const isEmptyResponse = publicData?.firstListingId === undefined || publicData?.firstListingId === null;

  let isFreshUser = false;
  if ((isNotCandidate && isNotAdmin && hasNotJoined) || (isEmptyResponse && (!isNotCandidate || !isNotAdmin))) {
    isFreshUser = true;
  }

  // Invite email mismatch detection (synchronous checks prevent flash of wrong content)
  const isSignupLinkTab = tab === 'signuplink';
  const invitedMail = typeof window !== 'undefined' ? window.localStorage.getItem('invitedMail') : null;
  const pendingInviteEmailCheck = isSignupLinkTab && isAuthenticated && !!invitedMail && !currentUserLoaded;
  const isInviteEmailMismatchOnConfirm = isConfirm && !!invitedMail && !!authInfo?.email &&
    invitedMail.toLowerCase() !== authInfo.email.toLowerCase();
  const isInviteEmailMismatchOnAuth = isSignupLinkTab && isAuthenticated && currentUserLoaded && !!invitedMail &&
    !!currentUser?.attributes?.email &&
    invitedMail.toLowerCase() !== currentUser.attributes.email.toLowerCase();

  // Handle existing authenticated user with invite email mismatch - logout and redirect
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isInviteEmailMismatchOnAuth) {
      window.localStorage.setItem('emailMismatchError', 'true');
      const storedInviteLink = window.localStorage.getItem('inviteLink');
      onLogout().then(() => {
        if (storedInviteLink) {
          window.location.href = `/signuplink?${storedInviteLink}`;
        }
      }).catch(() => {
        if (storedInviteLink) {
          window.location.href = `/signuplink?${storedInviteLink}`;
        }
      });
    } else if (isSignupLinkTab && isAuthenticated && currentUserLoaded && invitedMail &&
      currentUser?.attributes?.email &&
      invitedMail.toLowerCase() === currentUser.attributes.email.toLowerCase()) {
      // Emails match - clean up invite localStorage items
      window.localStorage.removeItem('invitedMail');
      window.localStorage.removeItem('inviteLink');
    }
  }, [isInviteEmailMismatchOnAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isSsoSignupUserTypeMismatch) return;

    window.localStorage.setItem(
      SSO_USER_TYPE_MISMATCH_ERROR_KEY,
      JSON.stringify({
        code: 'sso-user-type-mismatch',
        existingUserType,
        selectedUserType: ssoSelectedUserType,
        message: `This email is already registered under the ${existingUserType} user type. To sign up as ${ssoSelectedUserType}, please use a different email address.`,
      })
    );

    window.localStorage.removeItem(SSO_AUTH_FLOW_KEY);
    window.localStorage.removeItem(SSO_SELECTED_USER_TYPE_KEY);

    // Force a full document load so AuthenticationPage remounts and re-reads the
    // mismatch error from localStorage. The backend redirects SSO logins to
    // `/login#`, so we're usually already on that URL — assigning the same
    // `/login#` would only change the hash (no reload), leaving the error unread
    // and no message shown. Reload explicitly when already on the login page.
    const redirectToLoginWithError = () => {
      if (typeof window === 'undefined') return;
      if (window.location.pathname === '/login') {
        window.location.reload();
      } else {
        window.location.href = '/login#';
      }
    };

    onLogout()
      .then(redirectToLoginWithError)
      .catch(redirectToLoginWithError);
  }, [isSsoSignupUserTypeMismatch, existingUserType, ssoSelectedUserType, onLogout]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isAuthenticated || !currentUserLoaded || isSsoSignupUserTypeMismatch) return;

    window.localStorage.removeItem(SSO_AUTH_FLOW_KEY);
    window.localStorage.removeItem(SSO_SELECTED_USER_TYPE_KEY);
  }, [isAuthenticated, currentUserLoaded, isSsoSignupUserTypeMismatch]);

  // const redirectToDashboard = (isAuthenticated) || (!isNotCandidate && !isNotAdmin && !hasNotJoined);

  // We only want to show the email verification dialog in the signup
  // tab if the user isn't being redirected somewhere else
  // (i.e. `from` is present). We must also check the `emailVerified`
  // flag only when the current user is fully loaded.
  const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });
  const topbarClasses = classNames({
    [css.hideOnMobile]: showEmailVerification,
  });

  // Show blank page while handling invite email mismatch (prevents flash of wrong content)
  if (
    pendingInviteEmailCheck ||
    isInviteEmailMismatchOnConfirm ||
    isInviteEmailMismatchOnAuth ||
    pendingSsoSignupUserTypeCheck ||
    isSsoSignupUserTypeMismatch
  ) {
    return (
      <BlankPage
        schemaTitle={schemaTitle}
        schemaDescription={schemaDescription}
        topbarClasses={topbarClasses}
      />
    );
  }

  if (shouldShowAuthLoader) {
    return (
      <BlankPage
        schemaTitle={schemaTitle}
        schemaDescription={schemaDescription}
        topbarClasses={topbarClasses}
      />
    );
  }

  const shouldRedirectToFrom = isAuthenticated && from;
  const shouldRedirectToLandingPage =
    isAuthenticated && currentUserLoaded && !showEmailVerification;
  if (!mounted && shouldRedirectToLandingPage) {
    // Show a blank page for already authenticated users,
    // when the first rendering on client side is not yet done
    // This is done to avoid hydration issues when full page load is happening.
    return (
      <BlankPage
        schemaTitle={schemaTitle}
        schemaDescription={schemaDescription}
        topbarClasses={topbarClasses}
      />
    );
  }
  if (isAuthenticated && isFreshUser) {
    // Already authenticated, but has not completed onboarding, redirect to the profile settings page
    return <NamedRedirect name="ProfileSettingsPage" search={location.search} />;
  } else if (shouldRedirectToFrom) {
    // Already authenticated, redirect back to the page the user tried to access
    return <Redirect to={from} />;
    // } else if (redirectToDashboard) {
  } else if (isAuthenticated) {
    // Already authenticated, but does not have listing, redirect to the profile settings page
    // return <NamedRedirect name="ProfileSettingsPage" />;
    return <NamedRedirect name="DashboardPage" />;
    // } else if (shouldRedirectToLandingPage) {
    //   // Already authenticated, redirect to the landing page (this was direct access to /login or /signup)
    //   return <NamedRedirect name="LandingPage" />;
  } else if (show404) {
    // User type not found, show 404
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';
  const resendErrorMessage = sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer className={topbarClasses} />}
        footer={<FooterContainer />}
      >
        <ResponsiveBackgroundImageContainer
          className={css.root}
          childrenWrapperClassName={css.contentContainer}
          as="section"
          image={config.branding.brandImage}
          sizes="100%"
          useOverlay
        >
          {showEmailVerification ? (
            <EmailVerificationInfo
              name={user.attributes.profile.firstName}
              email={<span className={css.email}>{user.attributes.email}</span>}
              onResendVerificationEmail={onResendVerificationEmail}
              resendErrorMessage={resendErrorMessage}
              sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            />
          ) : (
            <AuthenticationOrConfirmInfoForm
              tab={tab}
              userType={userType}
              authInfo={authInfo}
              from={from}
              showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
              showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
              showLinkedInLogin={!!process.env.LINKEDIN_CLIENT_ID}
              showMicrosoftLogin={!!process.env.MICROSOFT_CLIENT_ID}
              submitLogin={submitLogin}
              submitSignup={submitSignup}
              submitSingupWithIdp={submitSingupWithIdp}
              authInProgress={authInProgress}
              loginError={loginError}
              idpAuthError={authError}
              signupError={signupError}
              confirmError={confirmError}
              onManageDisableScrolling={onManageDisableScrolling}
              termsAndConditions={
                <TermsAndConditions
                  onOpenTermsOfService={() => setTosModalOpen(true)}
                  onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                  intl={intl}
                />
              }
            />
          )}
        </ResponsiveBackgroundImageContainer>
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
    </Page>
  );
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    pageAssetsData: privacyAssetsData,
    inProgress: privacyFetchInProgress,
    error: privacyFetchError,
  } = state.hostedAssets || {};
  const { pageAssetsData: tosAssetsData, inProgress: tosFetchInProgress, error: tosFetchError } =
    state.hostedAssets || {};

  return {
    authInProgress: authenticationInProgress(state),
    authInfoLoaded: state.auth.authInfoLoaded,
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  onLogout: () => dispatch(logout()),
  onFetchAuthInfo: () => dispatch(authInfo()),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(AuthenticationPageComponent);

export default AuthenticationPage;
